import type { Carrier, Provider } from '../../settings-practice/types';

/**
 * Every number on Insurance & network is computed from the rows beneath it
 * (spec 15 §3.1). Nothing here is stored.
 */

export type CarrierStatus = 'in_network' | 'out_of_network' | 'not_taken' | 'paused';

/**
 * A carrier's status is worked out from its plans, never typed (§7.1).
 *
 * The built app lets a carrier be "Active" with nobody in network; that state is
 * impossible here — in network requires a taken plan that is in network.
 */
export function carrierStatus(c: Carrier): CarrierStatus {
  if (c.paused) return 'paused';
  const taken = c.plans.filter((p) => p.status !== 'not_taken');
  if (taken.length === 0) return 'not_taken';
  return taken.some((p) => p.status === 'in_network') && c.inNetworkProviderIds.length > 0
    ? 'in_network'
    : 'out_of_network';
}

/** The carriers the practice actually takes — paused ones are held, not counted. */
export function takenCarriers(carriers: Carrier[]): Carrier[] {
  return carriers.filter((c) => !c.paused);
}

/**
 * One provider's network, as the gaps rather than the matches — what a provider
 * cannot take is the shorter and more useful list (§7.2).
 *
 * `inNetwork.length + gaps.length` always equals the taken count, on every row.
 */
export function providerNetwork(p: Provider, carriers: Carrier[]): { inNetwork: Carrier[]; gaps: Carrier[] } {
  const taken = takenCarriers(carriers);
  return {
    inNetwork: taken.filter((c) => c.inNetworkProviderIds.includes(p.id)),
    gaps: taken.filter((c) => !c.inNetworkProviderIds.includes(p.id)),
  };
}

/** Total provider-and-carrier pairs in network, for the page footer. */
export function totalPairs(providers: Provider[], carriers: Carrier[]): number {
  return providers.reduce((n, p) => n + providerNetwork(p, carriers).inNetwork.length, 0);
}

/** How many plans on a carrier are taken, and how many of those are in network. */
export function planCounts(c: Carrier): { taken: number; total: number; inNetwork: number } {
  const taken = c.plans.filter((p) => p.status !== 'not_taken');
  return {
    taken: taken.length,
    total: c.plans.length,
    inNetwork: taken.filter((p) => p.status === 'in_network').length,
  };
}
